import * as React from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { postsUpdateFiltersScreenState, postsUpdateScreenState } from 'modules/insights/sov/reducers/posts/collabary/sov-posts';

import { __t, applyTranslations } from 'utils/language';
import { MULTI } from 'modules/insights/sov/utils/queries/sov-queries';

import './sov-posts-filters.scss';

import type {
  MixedSelectDropdownItem,
  SOVFilterCommon,
  SOVReportType
} from 'types';
import type { SOVQueryLocal } from 'modules/insights/sov/types';
import type { DebouncedFunc } from 'lodash';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./sov-posts-filters.i18n.json'));

const DEBOUNCE_DELAY = 250;

type OwnProps = {
  queriesMerged: Array<SOVQueryLocal>,
  selectedQueryIds: Array<number>,
  profileTypes: Array<SOVFilterCommon>,
  tiers: Array<SOVFilterCommon>,
  reportType: SOVReportType,
  onChange: () => void
};

type DispatchProps = {
  dispatchUpdateScreenState: (b: any, a: string) => void,
  dispatchUpdateFiltersScreenState: (b: any, a: string) => void
};

type Props = OwnProps & DispatchProps;

type State = {
  channel: string
};

const MSG_KEY = 'sov_posts_filters';

class SOVPostsFilters extends React.PureComponent<Props, State> {
  state = {
    channel: ''
  };

  debounceUpdate?: DebouncedFunc<(value: any, type: string) => void>;

  handleChange = (event: SyntheticEvent<HTMLSelectElement>): void => {
    const target = event.currentTarget;
    const value = target.value || '';
    const type = target.dataset.type || '';

    if (type) {
      this.setState<any>({
        [type]: value
      });

      // every time key is pressed, cancel previous debounce
      if (this.debounceUpdate) {
        this.debounceUpdate.cancel();
      }

      this.debounceUpdate = debounce(this.handleUpdate, type === 'channel' ? DEBOUNCE_DELAY : 0);
      this.debounceUpdate(value, type);
    }
  };

  handleUpdate = (value: any, type: string): void => {
    const { dispatchUpdateScreenState, onChange } = this.props;

    if (type !== 'sort') {
      dispatchUpdateScreenState(1, 'page');
    }

    dispatchUpdateScreenState(value, type);
    onChange();
  };

  handleKeywordUpdate = (updatedItems: Array<MixedSelectDropdownItem>): void => {
    const { dispatchUpdateFiltersScreenState, dispatchUpdateScreenState, onChange } = this.props;
    const items = updatedItems.filter(item => item.isSelected).map(item => item.id || 0);

    dispatchUpdateScreenState(1, 'page');
    dispatchUpdateFiltersScreenState(items, 'queryIds');
    onChange();
  };

  handleProfileUpdate = (updatedItems: Array<MixedSelectDropdownItem>): void => {
    const { dispatchUpdateFiltersScreenState, dispatchUpdateScreenState, onChange } = this.props;

    dispatchUpdateScreenState(1, 'page');
    dispatchUpdateFiltersScreenState(updatedItems, 'profileTypes');
    onChange();
  };

  handleTiersUpdate = (updatedItems: Array<MixedSelectDropdownItem>): void => {
    const { dispatchUpdateFiltersScreenState, dispatchUpdateScreenState, onChange } = this.props;

    dispatchUpdateScreenState(1, 'page');
    dispatchUpdateFiltersScreenState(updatedItems, 'tiers');
    onChange();
  };

  renderChannelField(): React.ReactNode {
    const { reportType } = this.props;
    const { channel } = this.state;

    return (
      <Input
        value={channel}
        placeholder={__t(`${MSG_KEY}.${reportType}.placeholder_channel`)}
        data={{ type: 'channel' }}
        onChange={this.handleChange}
      />
    );
  }

  renderChannelSearch(): React.ReactNode {
    return (
      <div className="sov-posts-filters__section sov-posts-filters__section-search">
        <div className="sov-posts-filters__section-title">
          {__t(`${MSG_KEY}.labels.search_by`)}:
        </div>
        <div className="sov-posts-filters__section-filter">
          {this.renderChannelField()}
        </div>
      </div>
    );
  }

  renderQueryDropdown(): React.ReactNode {
    const { queriesMerged, selectedQueryIds } = this.props;
    const filteredQueries = queriesMerged.filter(query => query.enabled);

    const data = filteredQueries.map(query => {
      const isMulti = query.type === MULTI;
      const updatedQueryId = isMulti
        ? query.ids.parent
        : query.id;

      return {
        id: updatedQueryId,
        identifier: (updatedQueryId || '').toString(),
        label: query.name || '',
        isSelected: selectedQueryIds.includes(updatedQueryId || 0),
        isDisabled: false,
        isFocused: false,
        value: (updatedQueryId || '').toString()
      };
    });

    return this.renderDropdown('keywords', true, data, this.handleKeywordUpdate);
  }

  renderFilterKeyword(): React.ReactNode {
    return (
      <div className="sov-posts-filters__section sov-posts-filters__section-filter-keyword">
        <div className="sov-posts-filters__section-title">
          {__t(`${MSG_KEY}.labels.filter_keyword`)}:
        </div>
        <div className="sov-posts-filters__section-filter">
          {this.renderQueryDropdown()}
        </div>
      </div>
    );
  }

  renderProfileTypesDropdown(): React.ReactNode {
    const { profileTypes } = this.props;

    const data = profileTypes.map(profileType => ({
      id: profileType.id,
      identifier: profileType.identifier,
      label: profileType.label || '',
      isSelected: profileType.isSelected,
      isDisabled: false,
      isFocused: false,
      value: profileType.value,
      icon: {
        secondary: {
          name: profileType.identifier === 'verified'
            ? 'shield-checkmark-inside-custom-bg-white-fg'
            : 'shield-custom-bg',
          width: 13,
          height: 16,
          color: profileType.identifier === 'verified'
            ? 'primary'
            : 'grey'
        }
      }
    }));

    return this.renderDropdown('profile-type', true, data, this.handleProfileUpdate);
  }

  renderFilterProfileType(): React.ReactNode {
    return (
      <div className="sov-posts-filters__section sov-posts-filters__section-filter-profile-type">
        <div className="sov-posts-filters__section-title">
          {__t(`${MSG_KEY}.labels.filter_profile_type`)}:
        </div>
        <div className="sov-posts-filters__section-filter">
          {this.renderProfileTypesDropdown()}
        </div>
      </div>
    );
  }

  renderTiersDropdown(): React.ReactNode {
    const { tiers } = this.props;

    const data = tiers.map(tier => ({
      id: tier.id,
      identifier: tier.identifier,
      label: tier.label || '',
      isSelected: tier.isSelected,
      isDisabled: false,
      isFocused: false,
      value: tier.value
    }));

    return this.renderDropdown('tiers', false, data, this.handleTiersUpdate);
  }

  renderFilterFollowers(): React.ReactNode {
    return (
      <div className="sov-posts-filters__section sov-posts-filters__section-filter-followers">
        <div className="sov-posts-filters__section-title">
          {__t(`${MSG_KEY}.labels.filter_followers`)}:
        </div>
        <div className="sov-posts-filters__section-filter">
          {this.renderTiersDropdown()}
        </div>
      </div>
    );
  }

  renderDropdown(
    key: string,
    sort: boolean,
    data: Array<MixedSelectDropdownItem>,
    handler: (updatedItems: Array<MixedSelectDropdownItem>) => void
  ): React.ReactNode {
    return (
      <div className="sov-posts-filters__filter-section">
        <div className={`sov-posts-filters__filter-${key}`}>
          <MixedSelectDropdown
            key={`sov-posts-filters__${key}-${data.filter(item => item.isSelected).join('-')}`}
            name={key}
            items={data}
            placeholder={__t(`${MSG_KEY}.labels.select`)}
            withMulti
            withRemovable
            withSortable={sort}
            withCheckboxes
            withIcons
            withTruncate={key === 'keywords'}
            onItemsToggle={handler}
            onItemsRemove={handler}
            onItemsRemoveAll={handler}
          />
        </div>
      </div>
    );
  }

  renderAdditionalFilters(): React.ReactNode {
    return (
      <React.Fragment>
        {this.renderFilterKeyword()}
        {this.renderFilterProfileType()}
        {this.renderFilterFollowers()}
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { reportType } = this.props;

    return (
      <div className="sov-posts-filters">
        <div className="sov-posts-filters__filters">
          {this.renderChannelSearch()}
          {
            reportType === 'instagram' &&
              this.renderAdditionalFilters()
          }
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateScreenState: (value: any, propName: string) => (
    dispatch(postsUpdateScreenState(value, propName))
  ),

  dispatchUpdateFiltersScreenState: (value: any, propName: string) => (
    dispatch(postsUpdateFiltersScreenState(value, propName))
  )
});

export default connect(null, mapDispatchToProps)(SOVPostsFilters);
